import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Col, Button, Table } from "reactstrap";
import { FadeLoader } from "react-spinners";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb2";
import axiosInstance from "pages/Utility/axiosInstance";
import { formatDateTimeToAmPm } from "helpers/dateFormat_helper";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const Dashboard = () => {
  const navigate = useNavigate(); 

  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTutors: 0,
    totalCourses: 0,
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  // ────────────────────────────────
  // Fetch dashboard data
  // ────────────────────────────────
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("V1/common/dashboard");
      if (res?.data?.success) {
        setStats({
          totalStudents: res.data.totalStudents || 0,
          totalTutors: res.data.totalTutors || 0,
          totalCourses: res.data.totalCourses || 0,
        });
        setRecentStudents(res.data.data || []);
      }
    } catch (error) {
      console.error("Dashboard fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };


const StatCardSkeleton = () => (
  <Card className="shadow-sm border-0 h-100">
    <CardBody>
      <div className="d-flex justify-content-between align-items-center">
      <div>
      <Skeleton height={18} width={130} />
      <Skeleton height={42} width={80} className="mt-2" />
      </div>
      <div className="d-flex justify-content-end mt-4">
        <Skeleton circle width={60} height={60} />
      </div>
      </div>
       <div className="d-flex justify-content-end">
         <Skeleton height={32} width={80} className="mt-2" />
       </div>
    </CardBody>
  </Card>
);

const TableSkeleton = ({ rows = 5 }) => (
  <Table bordered>
    <thead className="table-light">
      <tr>
        {Array.from({ length: 7 }).map((_, i) => (
          <th key={i}>
            <Skeleton height={20} />
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: 7 }).map((__, j) => (
            <td key={j}>
              <Skeleton height={18} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </Table>
);


  useEffect(() => {
    fetchDashboardData();
  }, []);

  // ────────────────────────────────
  // UI
  // ────────────────────────────────
  return (
    <div className="page-content container-fluid">
      <h4>Dashboard</h4>
      {loading ? (
      <>
        {/* Stat Cards Skeleton */}
        <Row className="mb-4 g-3">
          <Col xl={4} md={6} sm={12}>
            <StatCardSkeleton />
          </Col>
          <Col xl={4} md={6} sm={12}>
            <StatCardSkeleton />
          </Col>
          <Col xl={4} md={6} sm={12}>
            <StatCardSkeleton />
          </Col>
        </Row>

        {/* Table Skeleton */}
        <Card className="shadow-sm border-0">
          <CardBody>
            <Skeleton height={24} width={220} className="mb-3" />
            <TableSkeleton />
          </CardBody>
        </Card>
      </>
      ) : (
        <>
          {/* Top Stat Cards */}
          <Row className="mb-4 g-3">
            {/*  Students */}
            <Col xl={4} md={6} sm={12}>
              <Card
                className="shadow-sm border-0 h-100 dashboard-card"
                onClick={() => navigate("/students")}
                style={{ cursor: "pointer", transition: "all 0.2s ease" }}
              >
                <CardBody className="d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="fw-bold text-primary mb-1">
                        Total Students
                      </h5>
                      <h2 className="fw-bold text-primary mb-0">
                        {stats.totalStudents}
                      </h2>
                    </div>
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
                      style={{ width: 60, height: 60 }}
                    >
                      <i
                        className="mdi mdi-account-multiple-outline text-primary"
                        style={{ fontSize: "32px" }}
                      ></i>
                    </div>
                  </div>
                  <div className="text-end mt-3">
                    <span className="text-primary fw-semibold">
                      View All <i className="mdi mdi-arrow-right"></i>
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* 🎓 Tutors */}
            <Col xl={4} md={6} sm={12}>
              <Card
                className="shadow-sm border-0 h-100 dashboard-card"
                onClick={() => navigate("/tutors")}
                style={{ cursor: "pointer", transition: "all 0.2s ease" }}
              >
                <CardBody className="d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="fw-bold text-primary mb-1">
                        Total Tutors
                      </h5>
                      <h2 className="fw-bold text-success mb-0">
                        {stats.totalTutors}
                      </h2>
                    </div>
                    <div
                      className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center"
                      style={{ width: 60, height: 60 }}
                    >
                      <i
                        className="mdi mdi-school-outline text-success"
                        style={{ fontSize: "34px" }}
                      ></i>
                    </div>
                  </div>
                  <div className="text-end mt-3">
                    <span className="text-success fw-semibold">
                      View All <i className="mdi mdi-arrow-right"></i>
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* 📘 Courses */}
            <Col xl={4} md={6} sm={12}>
              <Card
                className="shadow-sm border-0 h-100 dashboard-card"
                onClick={() => navigate("/courses")}
                style={{ cursor: "pointer", transition: "all 0.2s ease" }}
              >
                <CardBody className="d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="fw-bold text-primary mb-1">
                        Total Courses
                      </h5>
                      <h2 className="fw-bold text-warning mb-0">
                        {stats.totalCourses}
                      </h2>
                    </div>
                    <div
                      className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center"
                      style={{ width: 60, height: 60 }}
                    >
                      <i
                        className="mdi mdi-book-open-variant text-warning"
                        style={{ fontSize: "34px" }}
                      ></i>
                    </div>
                  </div>
                  <div className="text-end mt-3">
                    <span className="text-warning fw-semibold">
                      View All <i className="mdi mdi-arrow-right"></i>
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Recent Enrollments */}
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">Recent Enrollments</h5>
                <Link to="/students">
                  <Button color="link" className="p-0 text-decoration-none">
                    View All <i className="mdi mdi-arrow-right"></i>
                  </Button>
                </Link>
              </div>

              <div className="table-responsive">
                <Table hover bordered>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Course</th>
                      <th>Mode</th>
                      <th>Enrolled On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentStudents.length > 0 ? (
                      recentStudents.map((student, index) => (
                        <tr key={student._id}>
                          <td>{index + 1}</td>
                          <td>{student.name}</td>
                          <td>{student.email}</td>
                          <td>{student.phone}</td>
                          <td>{student.course}</td>
                          <td className="text-capitalize">{student.mode}</td>
                          <td>
                            {formatDateTimeToAmPm(student.enrollmentDate)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No Enrollments Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
